/**
 * Appends log lines to a plain-text file so events survive host modes
 * that swallow stdout (e.g. TUI). Mirrors to `upstream` when provided.
 * Best-effort — write errors never block the caller.
 */
import { promises as fs } from "node:fs";
import path from "node:path";

/** Small logger shape used across the memory-poisoning surface. */
export interface WrappedLogger {
  info(message: string, meta?: Record<string, unknown>): void;
  warn(message: string, meta?: Record<string, unknown>): void;
}

interface UpstreamLogger {
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
}

function formatMeta(meta?: Record<string, unknown>): string {
  if (!meta || Object.keys(meta).length === 0) return "";
  try {
    return " " + JSON.stringify(meta);
  } catch {
    return "";
  }
}

/** Writes are serialized via an internal promise queue so lines interleave in call-order under concurrency. */
export function createFileLogger(
  filePath: string,
  upstream?: UpstreamLogger,
): WrappedLogger {
  let queue: Promise<void> = Promise.resolve();

  async function ensureDir(): Promise<void> {
    await fs.mkdir(path.dirname(filePath), { recursive: true });
  }

  function append(level: "info" | "warn", message: string, meta?: Record<string, unknown>): void {
    const line = `${new Date().toISOString()} [${level}] ${message}${formatMeta(meta)}\n`;
    queue = queue
      .then(ensureDir)
      .then(() => fs.appendFile(filePath, line, "utf8"))
      .catch(() => {});
  }

  return {
    info(message, meta) {
      upstream?.info(message, meta ?? {});
      append("info", message, meta);
    },
    warn(message, meta) {
      upstream?.warn(message, meta ?? {});
      append("warn", message, meta);
    },
  };
}

/** Default log-file location — `<workspaceDir>/.atbash/plugin.log`. */
export function defaultPluginLogPath(workspaceDir: string = process.cwd()): string {
  return path.join(workspaceDir, ".atbash", "plugin.log");
}
